import { style, globalStyle, keyframes } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { shimmer } from '../../keyframes/shared.css';
import { reducedMotion, mobile } from '../../theme/media';

export const content = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  minHeight: '200px',
  '@media': {
    [mobile]: {
      gap: '12px',
      minHeight: '160px',
    },
  },
});

export const questInfoCard = style({
  padding: '16px',
  borderRadius: '8px',
  background: vars.surface.highlight.faint,
  border: `1px solid ${vars.surface.border.secondary}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  '@media': {
    [mobile]: {
      padding: '12px',
      gap: '10px',
    },
  },
});

export const questTitle = style({
  fontSize: vars.typography.fontSize.lg,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.typography.textColor.primary,
  textAlign: 'center',
});

// 설명이 길어도 모달이 늘어나지 않게 높이를 제한하고 안에서 스크롤한다.
export const questDescription = style({
  textAlign: 'center',
  maxHeight: '180px',
  overflowY: 'auto',
});

// 자식 톤을 원래 모달 설명 타이포로 되돌린다(markdownBody 기본값은 워크로그 메모용).
globalStyle(`${questDescription} > *`, {
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.secondary,
  lineHeight: vars.typography.lineHeight.normal,
});

export const rewardPreview = style({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  padding: '12px',
  background: vars.opacityScale.whiteOverlay.faint,
  borderRadius: '6px',
  border: `1px solid ${vars.opacityScale.whiteOverlay.light}`,
  '@media': {
    [mobile]: {
      flexWrap: 'wrap',
      gap: '10px',
      padding: '10px',
    },
  },
});

export const rewardItem = style({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.semibold,
});

export const rewardAmount = style({
  fontFamily: vars.typography.fontFamily.mono,
  color: vars.bright.textHighlight.coin,
});

export const rewardLabel = style({
  color: vars.typography.textColor.secondary,
});

export const confirmMessage = style({
  textAlign: 'center',
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.tertiary,
  padding: '8px 0',
});

export const errorMessage = style({
  textAlign: 'center',
  fontSize: vars.typography.fontSize.sm,
  color: vars.bright.status.error,
  padding: '8px 0',
});

const pulseGlowShadow = keyframes({
  '0%, 100%': { boxShadow: `0 0 20px ${vars.surface.highlight.strong}` },
  '50%': { boxShadow: `0 0 40px ${vars.surface.highlight.strong}` },
});

const scaleIn = keyframes({
  '0%': { transform: 'scale(0.8)', opacity: '0' },
  '50%': { transform: 'scale(1.1)' },
  '100%': { transform: 'scale(1)', opacity: '1' },
});

const checkDraw = keyframes({
  '0%': { strokeDashoffset: '100' },
  '100%': { strokeDashoffset: '0' },
});

const fadeSlideUp = keyframes({
  '0%': { opacity: '0', transform: 'translateY(20px)' },
  '100%': { opacity: '1', transform: 'translateY(0)' },
});

export const acceptedContainer = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '24px',
  padding: '32px 16px',
  minHeight: '200px',
  '@media': {
    [mobile]: {
      gap: '16px',
      padding: '24px 12px',
      minHeight: '160px',
    },
  },
});

export const successIconWrapper = style({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${vars.surface.highlight.strong}, ${vars.surface.highlight.strong})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${scaleIn} ${vars.animation.duration.slow} ${vars.animation.easing.easeOut}, ${pulseGlowShadow} ${vars.animation.duration.xslow} ease-in-out infinite`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
      transform: 'scale(1)',
      opacity: 1,
      boxShadow: `0 0 20px ${vars.surface.highlight.strong}`,
    },
    [mobile]: {
      width: '64px',
      height: '64px',
    },
  },
});

export const successIcon = style({
  width: '40px',
  height: '40px',
  color: vars.bright.primary.default,
  '@media': {
    [mobile]: {
      width: '32px',
      height: '32px',
    },
  },
});

export const checkPath = style({
  strokeDasharray: '100',
  strokeDashoffset: '100',
  animation: `${checkDraw} ${vars.animation.duration.slow} ${vars.animation.easing.easeOut} ${vars.animation.duration.normal} forwards`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
      strokeDashoffset: '0',
    },
  },
});

export const acceptedTitle = style({
  fontSize: vars.typography.fontSize.xl,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.typography.textColor.primary,
  animation: `${fadeSlideUp} ${vars.animation.duration.normal} ${vars.animation.easing.easeOut} ${vars.animation.duration.sequenceLead} both`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

export const acceptedMessage = style({
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.secondary,
  textAlign: 'center',
  animation: `${fadeSlideUp} ${vars.animation.duration.normal} ${vars.animation.easing.easeOut} ${vars.animation.duration.slow} both`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

export const rewardBanner = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px 20px',
  borderRadius: '8px',
  flexWrap: 'wrap',
  background: `linear-gradient(90deg, ${vars.opacityScale.whiteOverlay.light} 0%, ${vars.opacityScale.whiteOverlay.strong} 50%, ${vars.opacityScale.whiteOverlay.light} 100%)`,
  backgroundSize: '200% 100%',
  border: `1px solid ${vars.opacityScale.whiteOverlay.strong}`,
  animation: `${fadeSlideUp} ${vars.animation.duration.normal} ${vars.animation.easing.easeOut} ${vars.animation.duration.sequenceTrail} both, ${shimmer} ${vars.animation.duration.crawl} linear infinite`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
      opacity: 1,
      transform: 'translateY(0)',
      backgroundPosition: '0% 0%',
    },
  },
});

export const rewardBannerText = style({
  fontSize: vars.typography.fontSize.md,
  fontWeight: vars.typography.fontWeight.semibold,
  color: vars.bright.textHighlight.coin,
});
